/**
 * LocalForage cache management utilities for photo uploads
 * These utilities are entity-agnostic and work with any entity type (assets, work tasks, etc.)
 */

import LocalForageInstance from 'common/infrastructure/storageLocal/LocalForage';
import loggerService, { LoggerLevel } from 'common/infrastructure/log';
import { CachedPhotoData, PendingPhotoLink } from './types';
import { Tp2Id, Tp3Id } from '@planitgeo/treeplotter-domain/lib/domain/types';

const OFFLINE_PHOTOS_KEY = 'offline_photo_uploads';
const PENDING_PHOTO_LINKS_KEY = 'pending_photo_links';
const ENTITY_TP2_IDS_KEY = 'entity_tp2_ids';

/**
 * Cache a photo for offline upload
 * @param entityId - The ID of the entity (asset, work task, etc.)
 * @param photoId - The unique photo ID
 * @param data - The cached photo data
 */
export async function cachePhoto(
  entityId: Tp3Id,
  photoId: Tp3Id,
  data: CachedPhotoData
): Promise<void> {
  loggerService.log(LoggerLevel.DEBUG, 'Caching photo', {
    entityId,
    photoId,
    fileName: data.name,
    fileSize: data.size,
    uploadStatus: data.uploadStatus,
    linkStatus: data.linkStatus,
  });

  // Acquire lock to prevent race conditions during read-modify-write
  const lock = await LocalForageInstance.getLock(OFFLINE_PHOTOS_KEY);

  try {
    const cachedPhotosJson =
      ((await LocalForageInstance.getItem(OFFLINE_PHOTOS_KEY, lock)) as string) || '{}';
    const cachedPhotos = JSON.parse(cachedPhotosJson);

    if (!cachedPhotos[entityId]) {
      cachedPhotos[entityId] = [];
    }

    // Check for duplicates before pushing
    const exists = cachedPhotos[entityId].some((photo: CachedPhotoData) => photo.id === photoId);

    if (!exists) {
      cachedPhotos[entityId].push(data);
      await LocalForageInstance.setItem(OFFLINE_PHOTOS_KEY, JSON.stringify(cachedPhotos), lock);

      loggerService.log(LoggerLevel.DEBUG, 'Photo cached successfully', {
        entityId,
        photoId,
        fileName: data.name,
        uploadStatus: data.uploadStatus,
        linkStatus: data.linkStatus,
      });
    } else {
      loggerService.log(LoggerLevel.DEBUG, 'Photo already cached, skipping duplicate', {
        entityId,
        photoId,
      });
    }
  } catch (error) {
    loggerService.log(LoggerLevel.ERROR, 'Failed to cache photo in localForage', {
      entityId,
      photoId,
      fileName: data.name,
      error: error instanceof Error ? error.message : 'Unknown error',
      errorStack: error instanceof Error ? error.stack : undefined,
    });
    throw new Error(
      `Failed to cache photo: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  } finally {
    // Always release lock
    LocalForageInstance.releaseLock(OFFLINE_PHOTOS_KEY);
  }
}

/**
 * Get all cached photos for an entity
 * @param entityId - The ID of the entity
 * @returns Array of cached photos
 */
export async function getPhotoCache(entityId: Tp3Id): Promise<CachedPhotoData[]> {
  try {
    const cachedPhotosJson =
      ((await LocalForageInstance.getItem(OFFLINE_PHOTOS_KEY)) as string) || '{}';
    const cachedPhotos = JSON.parse(cachedPhotosJson);
    return cachedPhotos[entityId] || [];
  } catch (error) {
    loggerService.log(LoggerLevel.ERROR, 'Failed to get cached photos from localForage', {
      entityId,
      error: error instanceof Error ? error.message : 'Unknown error',
      errorStack: error instanceof Error ? error.stack : undefined,
    });
    return [];
  }
}

/**
 * Get photos with pending link status for an entity
 * @param entityId - The ID of the entity
 * @returns Array of photos waiting to be linked
 */
export async function getPendingLinkPhotos(entityId: Tp3Id): Promise<CachedPhotoData[]> {
  const allPhotos = await getPhotoCache(entityId);
  return allPhotos.filter((photo) => photo.linkStatus === 'pending');
}

/**
 * Update a cached photo's properties
 * @param entityId - The ID of the entity
 * @param photoId - The photo ID to update
 * @param updates - Partial updates to apply
 */
export async function updatePhotoCache(
  entityId: Tp3Id,
  photoId: Tp3Id,
  updates: Partial<CachedPhotoData>
): Promise<void> {
  loggerService.log(LoggerLevel.DEBUG, 'Updating cached photo', {
    entityId,
    photoId,
    updates,
  });

  // Acquire lock to prevent race conditions during read-modify-write
  const lock = await LocalForageInstance.getLock(OFFLINE_PHOTOS_KEY);

  try {
    const cachedPhotosJson =
      ((await LocalForageInstance.getItem(OFFLINE_PHOTOS_KEY, lock)) as string) || '{}';
    const cachedPhotos = JSON.parse(cachedPhotosJson);

    if (cachedPhotos[entityId]) {
      const photoIndex = cachedPhotos[entityId].findIndex((p: CachedPhotoData) => p.id === photoId);

      if (photoIndex >= 0) {
        const previousState = cachedPhotos[entityId][photoIndex];
        cachedPhotos[entityId][photoIndex] = {
          ...cachedPhotos[entityId][photoIndex],
          ...updates,
        };

        await LocalForageInstance.setItem(OFFLINE_PHOTOS_KEY, JSON.stringify(cachedPhotos), lock);

        loggerService.log(LoggerLevel.DEBUG, 'Cached photo updated successfully', {
          entityId,
          photoId,
          previousState: {
            uploadStatus: previousState.uploadStatus,
            linkStatus: previousState.linkStatus,
          },
          newState: {
            uploadStatus: cachedPhotos[entityId][photoIndex].uploadStatus,
            linkStatus: cachedPhotos[entityId][photoIndex].linkStatus,
          },
        });
      } else {
        loggerService.log(LoggerLevel.WARN, 'Photo not found in cache for update', {
          entityId,
          photoId,
        });
      }
    }
  } catch (error) {
    loggerService.log(LoggerLevel.ERROR, 'Failed to update cached photo in localForage', {
      entityId,
      photoId,
      updates,
      error: error instanceof Error ? error.message : 'Unknown error',
      errorStack: error instanceof Error ? error.stack : undefined,
    });
  } finally {
    // Always release lock
    LocalForageInstance.releaseLock(OFFLINE_PHOTOS_KEY);
  }
}

/**
 * Remove a cached photo
 * @param entityId - The ID of the entity
 * @param photoId - The photo ID to remove
 */
export async function removePhotoFromCache(entityId: Tp3Id, photoId: Tp3Id): Promise<void> {
  loggerService.log(LoggerLevel.DEBUG, 'Removing cached photo', {
    entityId,
    photoId,
  });

  // Acquire lock to prevent race conditions during read-modify-write
  const lock = await LocalForageInstance.getLock(OFFLINE_PHOTOS_KEY);

  try {
    const cachedPhotosJson =
      ((await LocalForageInstance.getItem(OFFLINE_PHOTOS_KEY, lock)) as string) || '{}';
    const cachedPhotos = JSON.parse(cachedPhotosJson);

    if (cachedPhotos[entityId]) {
      const initialCount = cachedPhotos[entityId].length;
      cachedPhotos[entityId] = cachedPhotos[entityId].filter(
        (p: CachedPhotoData) => p.id !== photoId
      );

      if (cachedPhotos[entityId].length === 0) {
        delete cachedPhotos[entityId];
      }

      await LocalForageInstance.setItem(OFFLINE_PHOTOS_KEY, JSON.stringify(cachedPhotos), lock);

      loggerService.log(LoggerLevel.DEBUG, 'Cached photo removed successfully', {
        entityId,
        photoId,
        previousCount: initialCount,
        newCount: cachedPhotos[entityId]?.length || 0,
      });
    } else {
      loggerService.log(LoggerLevel.WARN, 'No cached photos found for entity during removal', {
        entityId,
        photoId,
      });
    }
  } catch (error) {
    loggerService.log(LoggerLevel.ERROR, 'Failed to remove cached photo from localForage', {
      entityId,
      photoId,
      error: error instanceof Error ? error.message : 'Unknown error',
      errorStack: error instanceof Error ? error.stack : undefined,
    });
  } finally {
    // Always release lock
    LocalForageInstance.releaseLock(OFFLINE_PHOTOS_KEY);
  }
}

/**
 * Register a pending photo link (for photos uploaded before entity has a TP2 ID)
 * @param entityId - The ID of the entity
 * @param photoId - The photo ID
 * @param metadata - Additional metadata needed for linking (e.g., assetClassId)
 */
export async function registerPendingPhotoLink(
  entityId: Tp3Id,
  photoId: Tp3Id,
  metadata: Record<string, string>
): Promise<void> {
  loggerService.log(LoggerLevel.DEBUG, 'Registering pending photo link', {
    entityId,
    photoId,
    metadata,
  });

  // Acquire lock to prevent race conditions during read-modify-write
  const lock = await LocalForageInstance.getLock(PENDING_PHOTO_LINKS_KEY);

  try {
    const pendingLinksJson =
      ((await LocalForageInstance.getItem(PENDING_PHOTO_LINKS_KEY, lock)) as string) || '{}';
    const pendingLinks = JSON.parse(pendingLinksJson);

    if (!pendingLinks[entityId]) {
      pendingLinks[entityId] = [];
    }

    const pendingLink: PendingPhotoLink = {
      photoId,
      entityId,
      metadata,
    };

    const exists = pendingLinks[entityId].some(
      (link: PendingPhotoLink) => link.photoId === photoId
    );
    if (!exists) {
      pendingLinks[entityId].push(pendingLink);
      await LocalForageInstance.setItem(
        PENDING_PHOTO_LINKS_KEY,
        JSON.stringify(pendingLinks),
        lock
      );

      loggerService.log(LoggerLevel.DEBUG, 'Pending photo link registered successfully', {
        entityId,
        photoId,
        metadata,
        totalPendingLinks: pendingLinks[entityId].length,
      });
    } else {
      loggerService.log(LoggerLevel.DEBUG, 'Pending photo link already exists', {
        entityId,
        photoId,
      });
    }
  } catch (error) {
    loggerService.log(LoggerLevel.ERROR, 'Failed to register pending photo link', {
      entityId,
      photoId,
      metadata,
      error: error instanceof Error ? error.message : 'Unknown error',
      errorStack: error instanceof Error ? error.stack : undefined,
    });
    throw new Error(
      `Failed to register pending photo link: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    );
  } finally {
    // Always release lock
    LocalForageInstance.releaseLock(PENDING_PHOTO_LINKS_KEY);
  }
}

/**
 * Get all pending photo links for an entity
 * @param entityId - The ID of the entity
 * @returns Array of pending photo links
 */
export async function getPendingPhotoLinks(entityId: Tp3Id): Promise<PendingPhotoLink[]> {
  try {
    const pendingLinksJson =
      ((await LocalForageInstance.getItem(PENDING_PHOTO_LINKS_KEY)) as string) || '{}';
    const pendingLinks = JSON.parse(pendingLinksJson);
    return pendingLinks[entityId] || [];
  } catch (error) {
    loggerService.log(LoggerLevel.ERROR, 'Failed to get pending photo links from localForage', {
      entityId,
      error: error instanceof Error ? error.message : 'Unknown error',
      errorStack: error instanceof Error ? error.stack : undefined,
    });
    return [];
  }
}

/**
 * Clear all pending photo links for an entity
 * @param entityId - The ID of the entity
 */
export async function clearPendingPhotoLinks(entityId: Tp3Id): Promise<void> {
  loggerService.log(LoggerLevel.DEBUG, 'Clearing pending photo links', {
    entityId,
  });

  // Acquire lock to prevent race conditions during read-modify-write
  const lock = await LocalForageInstance.getLock(PENDING_PHOTO_LINKS_KEY);

  try {
    const pendingLinksJson =
      ((await LocalForageInstance.getItem(PENDING_PHOTO_LINKS_KEY, lock)) as string) || '{}';
    const pendingLinks = JSON.parse(pendingLinksJson);

    if (pendingLinks[entityId]) {
      const clearedCount = pendingLinks[entityId].length;
      delete pendingLinks[entityId];
      await LocalForageInstance.setItem(
        PENDING_PHOTO_LINKS_KEY,
        JSON.stringify(pendingLinks),
        lock
      );

      loggerService.log(LoggerLevel.DEBUG, 'Pending photo links cleared successfully', {
        entityId,
        clearedCount,
      });
    } else {
      loggerService.log(LoggerLevel.DEBUG, 'No pending photo links found for entity', {
        entityId,
      });
    }
  } catch (error) {
    loggerService.log(LoggerLevel.ERROR, 'Failed to clear pending photo links', {
      entityId,
      error: error instanceof Error ? error.message : 'Unknown error',
      errorStack: error instanceof Error ? error.stack : undefined,
    });
  } finally {
    // Always release lock
    LocalForageInstance.releaseLock(PENDING_PHOTO_LINKS_KEY);
  }
}

/**
 * Records an entity's tp2 id, so a photo upload that finishes after the entity was
 * created can resolve it and link itself.
 */
export async function recordEntityTp2Id(entityId: Tp3Id, tp2AssetId: Tp2Id): Promise<void> {
  const lock = await LocalForageInstance.getLock(ENTITY_TP2_IDS_KEY);

  try {
    const json = ((await LocalForageInstance.getItem(ENTITY_TP2_IDS_KEY, lock)) as string) || '{}';
    const ids = JSON.parse(json);
    ids[entityId] = tp2AssetId;
    await LocalForageInstance.setItem(ENTITY_TP2_IDS_KEY, JSON.stringify(ids), lock);
  } catch (error) {
    loggerService.log(LoggerLevel.ERROR, 'Failed to record entity tp2 id', {
      entityId,
      tp2AssetId,
      error: error instanceof Error ? error.message : 'Unknown error',
    });
  } finally {
    LocalForageInstance.releaseLock(ENTITY_TP2_IDS_KEY);
  }
}

export async function getEntityTp2Id(entityId: Tp3Id): Promise<Tp2Id | undefined> {
  try {
    const json = ((await LocalForageInstance.getItem(ENTITY_TP2_IDS_KEY)) as string) || '{}';
    const value = JSON.parse(json)[entityId];
    return typeof value === 'number' ? value : undefined;
  } catch (error) {
    loggerService.log(LoggerLevel.ERROR, 'Failed to read entity tp2 id', {
      entityId,
      error: error instanceof Error ? error.message : 'Unknown error',
    });
    return undefined;
  }
}

export async function clearEntityTp2Id(entityId: Tp3Id): Promise<void> {
  const lock = await LocalForageInstance.getLock(ENTITY_TP2_IDS_KEY);

  try {
    const json = ((await LocalForageInstance.getItem(ENTITY_TP2_IDS_KEY, lock)) as string) || '{}';
    const ids = JSON.parse(json);
    if (entityId in ids) {
      delete ids[entityId];
      await LocalForageInstance.setItem(ENTITY_TP2_IDS_KEY, JSON.stringify(ids), lock);
    }
  } catch (error) {
    loggerService.log(LoggerLevel.ERROR, 'Failed to clear entity tp2 id', {
      entityId,
      error: error instanceof Error ? error.message : 'Unknown error',
    });
  } finally {
    LocalForageInstance.releaseLock(ENTITY_TP2_IDS_KEY);
  }
}
